'use client';

import classNames from 'classnames';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';
import TrailDetailsStickyNavSection from '@alltrails/amplitude/enums/TrailDetailsStickyNavSection';
import logTrailDetailsStickyNavClicked from '@alltrails/amplitude/events/logTrailDetailsStickyNavClicked';
import useIntersectionObserverCallback from '@alltrails/core/hooks/useIntersectionObserverCallback';
import useMediaQuery from '@alltrails/core/hooks/useMediaQuery';
import TabBar from '@alltrails/denali/components/TabBar';
import CreateEventButton from '@alltrails/events/components/CreateEventButton';
import { useExperiments } from '@alltrails/experiments';
import { DirectionsCta, ShareCta, TrailPage } from '@alltrails/trails';
import type TrailDetails from '@alltrails/types/TrailDetails';
import SaveCta from '@/components/SaveCta';
import {
  CONDITIONS_SECTION_ID,
  NEARBY_TRAILS_SECTION_ID,
  OVERVIEW_SECTION_ID,
  REVIEWS_SECTION_ID,
  SEGMENTS_SECTION_ID
} from '@/utils/constants';
import getTrailPageType from '@/utils/getTrailPageType';
import { useAppDispatch, useAppSelector } from '@/utils/redux';
import SectionNavId from '@/utils/SectionNavId';
import { setActiveSection, setIsScrollingViaTabs } from '@/utils/trailPageSlice';
import CustomizeRouteButton from '../Overview/CustomizeRouteButton';
import styles from './DesktopNav.module.scss';
import OverflowMenuVariant from './OverflowMenuVariant';

function getAnalyticsNavSection(tabId: SectionNavId): TrailDetailsStickyNavSection | undefined {
  switch (tabId) {
    case OVERVIEW_SECTION_ID:
      return 'overview';
    case CONDITIONS_SECTION_ID:
      return 'conditions';
    case REVIEWS_SECTION_ID:
      return 'reviews';
    case NEARBY_TRAILS_SECTION_ID:
      return 'nearby';
    case SEGMENTS_SECTION_ID:
      return 'route segments';
    default:
      return undefined;
  }
}

type DesktopNavProps = {
  trailDetails: TrailDetails;
  hasAdminToolbar: boolean;
  hasSegments?: boolean;
  trailPage: TrailPage;
  seoTestHideAllReviewsForLoggedOutUsers?: boolean;
};

const DesktopNav = ({ trailDetails, trailPage, hasAdminToolbar, hasSegments, seoTestHideAllReviewsForLoggedOutUsers }: DesktopNavProps) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const experiments = useExperiments();
  const stickyNavRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLDivElement>(null);
  const ctasRef = useRef<HTMLDivElement>(null);
  const smallNav = useMediaQuery('(max-width: 960px)');
  const mediumNav = useMediaQuery('(max-width: 1120px)');
  const { id } = trailDetails;

  const isExperimentTreatment = experiments['web-growth-rm-trail-page-ctas']?.value === 'treatment';

  const activeSection = useAppSelector(state => state.trailPage.activeSection);

  const stickyObserver = useIntersectionObserverCallback(
    ([e]) => {
      const isSticking = e.intersectionRatio < 1;
      e.target.classList.toggle(styles.sticking, isSticking);
      navRef.current?.classList.toggle(styles.navSticking, isSticking);
      ctasRef.current?.classList.toggle(styles.ctasSticking, isSticking);
    },
    { threshold: 1 }
  );

  const sectionLinks = useMemo(() => {
    const links = [
      { id: OVERVIEW_SECTION_ID, label: intl.formatMessage({ defaultMessage: 'Overview' }) },
      ...(hasSegments ? [{ id: SEGMENTS_SECTION_ID, label: intl.formatMessage({ defaultMessage: 'Route segments' }) }] : []),
      { id: CONDITIONS_SECTION_ID, label: intl.formatMessage({ defaultMessage: 'Conditions' }) },
      ...(seoTestHideAllReviewsForLoggedOutUsers ? [] : [{ id: REVIEWS_SECTION_ID, label: intl.formatMessage({ defaultMessage: 'Reviews' }) }]),
      { id: NEARBY_TRAILS_SECTION_ID, label: intl.formatMessage({ defaultMessage: 'Nearby' }) }
    ];

    if (smallNav) {
      const hiddenSectionIds = [SEGMENTS_SECTION_ID, NEARBY_TRAILS_SECTION_ID];
      return links.filter(link => !hiddenSectionIds.includes(link.id));
    } else if (mediumNav) {
      const hiddenSectionIds = [NEARBY_TRAILS_SECTION_ID];
      return links.filter(link => !hiddenSectionIds.includes(link.id));
    }

    return links;
  }, [intl, hasSegments, seoTestHideAllReviewsForLoggedOutUsers, smallNav, mediumNav]);

  const onTabChange = useCallback(
    (tabId: string) => {
      const navSection = getAnalyticsNavSection(tabId as SectionNavId);
      if (navSection) {
        logTrailDetailsStickyNavClicked({
          current_nav_section: getAnalyticsNavSection(activeSection),
          section_clicked: navSection,
          trail_id: id
        });
      }
      dispatch(setActiveSection(tabId as SectionNavId));
      dispatch(setIsScrollingViaTabs(true));

      window.addEventListener(
        'scrollend',
        () => {
          dispatch(setIsScrollingViaTabs(false));
        },
        { once: true }
      );
      document.getElementById(tabId)?.scrollIntoView({ behavior: 'smooth' });
    },
    [activeSection, dispatch, id]
  );

  useEffect(() => {
    const stickyNav = stickyNavRef.current;
    if (stickyNav && stickyObserver) {
      stickyObserver.observe(stickyNav);
    }

    return () => {
      stickyObserver?.disconnect();
    };
  }, [stickyObserver]);

  const trailPageType = getTrailPageType(trailDetails);

  return (
    <div className={classNames(styles.stickyNavContainer, { [styles.hasAdminToolbar]: hasAdminToolbar })} ref={stickyNavRef}>
      <div className={styles.stickyNav} ref={navRef}>
        <TabBar
          activeTab={activeSection}
          className={styles.tabBar}
          hideBottomBorder
          indicatorPosition="top"
          onChange={onTabChange}
          tabs={sectionLinks}
        />
        <div className={styles.ctas} ref={ctasRef}>
          <CustomizeRouteButton
            trailSlug={trailDetails.slug}
            trailId={trailDetails.id}
            analyticsLocation="trail details navbar"
            isDesktopCta
            trailPageType={trailPageType}
          />

          {!isExperimentTreatment && <SaveCta buttonLocation="header bar" trailId={id} trailPageType={trailPageType} />}

          <ShareCta trailDetails={trailDetails} buttonLocation="header bar" trailPageType={trailPageType} />

          <CreateEventButton mapId={id} mapType="T" />

          {!isExperimentTreatment && (
            <DirectionsCta trailId={id} location={trailDetails.location} buttonLocation="header bar" trailPageType={trailPageType} />
          )}

          {isExperimentTreatment && <OverflowMenuVariant trailDetails={trailDetails} trailPage={trailPage} />}
        </div>
      </div>
    </div>
  );
};

export default DesktopNav;
